import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Building2, DoorOpen, Users, FileText, Receipt, Wrench,
  Truck, TrendingUp, Landmark, Calculator, FolderOpen, Bot, BarChart3,
  Settings as SettingsIcon, ChevronDown, DoorClosed, Wallet, AlertTriangle,
  Bell, ShieldCheck, Building, KeyRound, CreditCard,
} from "lucide-react";

export const NAV = [
  { to: "/dashboard", label: "ダッシュボード", icon: LayoutDashboard },
  {
    label: "物件管理", icon: Building2, key: "property",
    children: [
      { to: "/properties", label: "物件一覧", icon: Building },
      { to: "/rooms", label: "部屋一覧", icon: DoorOpen },
      { to: "/vacancies", label: "空室一覧", icon: DoorClosed },
      { to: "/repairs", label: "修繕管理", icon: Wrench },
    ],
  },
  { to: "/tenants", label: "入居者管理", icon: Users },
  { to: "/landlords", label: "貸主管理", icon: Landmark },
  { to: "/tenant-access", label: "借主ポータル", icon: KeyRound },
  { to: "/contracts", label: "契約管理", icon: FileText },
  {
    label: "家賃管理", icon: Receipt, key: "rent",
    children: [
      { to: "/invoices", label: "請求", icon: Receipt },
      { to: "/payments", label: "入金", icon: Wallet },
      { to: "/arrears", label: "滞納", icon: AlertTriangle },
    ],
  },
  { to: "/vendors", label: "業者管理", icon: Truck },
  { to: "/finance", label: "収支管理", icon: TrendingUp },
  { to: "/loans", label: "ローン管理", icon: Landmark },
  { to: "/investment", label: "投資分析", icon: Calculator },
  { to: "/documents", label: "書類管理", icon: FolderOpen },
  { to: "/ai", label: "AIアシスタント", icon: Bot },
  { to: "/reports", label: "レポート", icon: BarChart3 },
  {
    label: "設定", icon: SettingsIcon, key: "settings",
    children: [
      { to: "/settings/company", label: "会社設定", icon: Building },
      { to: "/settings/billing", label: "プラン・お支払い", icon: CreditCard },
      { to: "/settings/users", label: "ユーザー", icon: Users },
      { to: "/settings/roles", label: "権限", icon: ShieldCheck },
      { to: "/settings/emails", label: "メール通知", icon: Bell },
      { to: "/notifications", label: "通知", icon: Bell },
      { to: "/settings/audit", label: "監査ログ", icon: FileText },
    ],
  },
];

export const SAAS_NAV = [
  { to: "/dashboard", label: "ダッシュボード", icon: LayoutDashboard },
  { to: "/admin/companies", label: "運営管理", icon: ShieldCheck },
  {
    label: "設定", icon: SettingsIcon, key: "settings",
    children: [
      { to: "/settings/billing", label: "プラン・お支払い", icon: CreditCard },
      { to: "/settings/users", label: "ユーザー", icon: Users },
      { to: "/settings/roles", label: "権限", icon: ShieldCheck },
      { to: "/settings/emails", label: "メール通知", icon: Bell },
      { to: "/notifications", label: "通知", icon: Bell },
      { to: "/settings/audit", label: "監査ログ", icon: FileText },
    ],
  },
];

function itemActive(pathname, to) {
  if (to === "/") return pathname === "/";
  return pathname.startsWith(to);
}

export function Sidebar({ onNavigate }) {
  const location = useLocation();
  const { t } = useI18n();
  const { user } = useAuth();
  const navItems = user?.role === "system_admin" ? SAAS_NAV : NAV;
  const [open, setOpen] = useState(() => {
    const s = {};
    navItems.forEach((n) => {
      if (n.children) s[n.key] = n.children.some((c) => itemActive(location.pathname, c.to));
    });
    return s;
  });

  const toggle = (key) => setOpen((p) => ({ ...p, [key]: !p[key] }));

  return (
    <aside className="w-64 shrink-0 bg-[#111111] text-gray-300 h-full flex flex-col">
      <div className="h-16 flex items-center gap-2.5 px-5 border-b border-white/10 shrink-0">
        <img src="/logo.png" alt="Neo Real Estate Management SaaS" className="h-9 w-auto object-contain bg-white rounded-md p-1 shrink-0" />
        <div className="leading-tight">
          <div className="font-display font-semibold text-white text-sm tracking-wide">NEO ESTATE</div>
          <div className="text-[10px] text-gray-500 tracking-wider">{t("不動産管理システム")}</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2.5 space-y-0.5">
        {navItems.map((item) => {
          if (item.children) {
            const isOpen = open[item.key];
            return (
              <div key={item.key}>
                <button
                  onClick={() => toggle(item.key)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-sm text-sm hover:bg-white/5 transition-colors"
                  data-testid={`nav-group-${item.key}`}
                >
                  <span className="flex items-center gap-3">
                    <item.icon className="w-[18px] h-[18px]" />
                    {t(item.label)}
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="mt-0.5 ml-3 pl-3 border-l border-white/10 space-y-0.5">
                    {item.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 px-3 py-2 rounded-sm text-[13px] transition-colors ${
                            isActive ? "bg-brand text-white" : "text-gray-400 hover:bg-white/5 hover:text-white"
                          }`
                        }
                        data-testid={`nav-${c.to}`}
                      >
                        <c.icon className="w-4 h-4" />
                        {t(c.label)}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              end={item.to === "/"}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                  isActive ? "bg-brand text-white" : "text-gray-300 hover:bg-white/5 hover:text-white"
                }`
              }
              data-testid={`nav-${item.to}`}
            >
              <item.icon className="w-[18px] h-[18px]" />
              {t(item.label)}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
